import {
  Avatar,
  AvatarFallbackText,
  AvatarImage,
} from "@repo/components/src/ui/avatar";
import { Box } from "@repo/components/src/ui/box";
import { HStack } from "@repo/components/src/ui/hstack";
import { Pressable } from "@repo/components/src/ui/pressable";
import { Text } from "@repo/components/src/ui/text";
import { VStack } from "@repo/components/src/ui/vstack";
import { formatPercentage } from "@repo/components/src/utils/numbers";
import { CommentFragmentFragment } from "@repo/graphql/documents/campaign/fragment.generated";
import { MontuCommentSentimentChoices } from "@repo/graphql/gql/graphql";
import dayjs from "dayjs";

export type SentimentFilter = "all" | "pos" | "neu" | "neg";

export type SentimentDistribution = {
  positive: number;
  neutral: number;
  negative: number;
  analyzed: number;
};

export const SentimentBar = ({
  distribution,
}: {
  distribution: SentimentDistribution;
}) => {
  const { positive, neutral, negative, analyzed } = distribution;
  const total = analyzed > 0 ? analyzed : positive + neutral + negative;
  const pct = (n: number) => (total > 0 ? (n / total) * 100 : 0);
  return (
    <VStack space="xs">
      <Box className="h-2 rounded-full overflow-hidden bg-background-100">
        <HStack className="h-full w-full">
          <Box
            className="bg-success-500 h-full"
            style={{ width: `${pct(positive)}%` } as object}
          />
          <Box
            className="bg-typography-400 h-full"
            style={{ width: `${pct(neutral)}%` } as object}
          />
          <Box
            className="bg-error-500 h-full"
            style={{ width: `${pct(negative)}%` } as object}
          />
        </HStack>
      </Box>
      <HStack space="md" className="flex-wrap">
        <LegendDot
          color="bg-success-500"
          label="Positivo"
          pct={pct(positive)}
        />
        <LegendDot
          color="bg-typography-400"
          label="Neutro"
          pct={pct(neutral)}
        />
        <LegendDot color="bg-error-500" label="Negativo" pct={pct(negative)} />
      </HStack>
    </VStack>
  );
};

const LegendDot = ({
  color,
  label,
  pct,
}: {
  color: string;
  label: string;
  pct: number;
}) => (
  <HStack space="xs" className="items-center">
    <Box className={`w-2 h-2 rounded-full ${color}`} />
    <Text size="xs" className="text-typography-500">
      {label}
    </Text>
    <Text size="xs" className="text-typography-900 font-semibold">
      {formatPercentage(pct / 100, 0)}
    </Text>
  </HStack>
);

export const SentimentTabs = ({
  value,
  onChange,
  compact,
}: {
  value: SentimentFilter;
  onChange: (next: SentimentFilter) => void;
  compact?: boolean;
}) => {
  const options: { value: SentimentFilter; label: string }[] = compact
    ? [
        { value: "all", label: "Todos" },
        { value: "pos", label: "+" },
        { value: "neu", label: "~" },
        { value: "neg", label: "−" },
      ]
    : [
        { value: "all", label: "Todos" },
        { value: "pos", label: "Positivos" },
        { value: "neu", label: "Neutros" },
        { value: "neg", label: "Negativos" },
      ];
  return (
    <Box className="bg-background-100 rounded-lg p-1">
      <HStack space="xs">
        {options.map((opt) => {
          const active = value === opt.value;
          return (
            <Pressable
              key={opt.value}
              onPress={() => onChange(opt.value)}
              className={
                active
                  ? "px-3 py-1 rounded-md bg-background-0"
                  : "px-3 py-1 rounded-md"
              }
            >
              <Text
                size="xs"
                className={
                  active
                    ? "text-typography-900 font-semibold"
                    : "text-typography-500 font-semibold"
                }
              >
                {opt.label}
              </Text>
            </Pressable>
          );
        })}
      </HStack>
    </Box>
  );
};

export const SENTIMENT_PILL: Record<
  MontuCommentSentimentChoices,
  { label: string; className: string }
> = {
  [MontuCommentSentimentChoices.Positive]: {
    label: "+ POS",
    className: "bg-success-50 text-success-700",
  },
  [MontuCommentSentimentChoices.Neutral]: {
    label: "~ NEU",
    className: "bg-background-100 text-typography-500",
  },
  [MontuCommentSentimentChoices.Negative]: {
    label: "− NEG",
    className: "bg-error-50 text-error-700",
  },
};

const formatRelative = (raw: string | null | undefined) => {
  if (!raw) return null;
  const d = dayjs(raw);
  if (!d.isValid()) return null;
  return d.fromNow();
};

// The fields CommentRow renders, taken from the paginated `campaignComments`
// list (CommentType).
export type CommentRowData = Pick<
  CommentFragmentFragment,
  "authorUsername" | "authorAvatarUrl" | "text" | "postedAt" | "sentiment"
>;

export const CommentRow = ({ comment }: { comment: CommentRowData }) => {
  const pill = comment.sentiment?.enum
    ? SENTIMENT_PILL[comment.sentiment.enum]
    : null;
  const when = formatRelative(comment.postedAt);
  return (
    <Box className="bg-background-0 border border-outline-100 rounded-xl px-3 py-2">
      <HStack space="sm" className="items-start">
        <Avatar size="xs">
          <AvatarFallbackText>{comment.authorUsername}</AvatarFallbackText>
          <AvatarImage source={{ uri: comment.authorAvatarUrl }} />
        </Avatar>
        <VStack className="flex-1" space="xs">
          <Text size="sm" className="text-typography-900">
            <Text size="sm" className="text-typography-900 font-semibold">
              {comment.authorUsername}{" "}
            </Text>
            {comment.text}
          </Text>
          {when ? (
            <Text size="2xs" className="text-typography-400">
              {when}
            </Text>
          ) : null}
        </VStack>
        {pill ? (
          <Box className={`${pill.className} rounded-full px-2 py-0.5`}>
            <Text size="2xs" className="font-bold">
              {pill.label}
            </Text>
          </Box>
        ) : null}
      </HStack>
    </Box>
  );
};
